import { yupResolver } from "@hookform/resolvers/yup";
import { Box } from "@material-ui/core";
import MovieIcon from "@material-ui/icons/Movie";
import { getEmbededDetails } from "adapters";
import { FileReader } from "components/shared/file-reader";
import { Input } from "components/shared/input";
import React, { Fragment, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import ReactPlayer from "react-player";
import { toast } from "react-toastify";
import { ResourceType } from "enums/videoType";
import { UploadType } from "store/url-builder";
import {
  InstagramEmbed,
  FacebookEmbed,
  TikTokEmbed,
  TwitterEmbed,
  PinterestEmbed,
} from "react-social-media-embed";
import {
  isInstagram,
  isFacebookReel,
  isFacebookVideo,
  isFacebookShareUrl,
  getFacebookVideoEmbedUrl,
  isTikTok,
  isTwitter,
  isLinkedIn,
  isPinterest,
  getLinkedInEmbedUrl,
} from "utils";
import * as yup from "yup";

const schema = yup.object().shape({
  fileUrl: yup.string().url().required().label("File URL"),
  redirectLink: yup.string().url().nullable().optional().label("Link URL"),
});

interface FormValues {
  fileUrl: string;
  redirectLink?: string;
}

export interface EmbededFileImportProps {
  onFileChanged: (file: UploadType) => void;
  onUrlChanged?: (url: string | null) => void;
}

export const EmbededFileImport = ({
  onFileChanged,
  onUrlChanged,
}: EmbededFileImportProps) => {
  const [selectedFile, setSelectedFile] = useState<UploadType | null>();
  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    mode: "all",
    defaultValues: {
      fileUrl: "",
      redirectLink: "",
    },
  });

  const redirectLink = watch("redirectLink");
  const previewUrl = watch("fileUrl");

  // Update parent when optional link URL changes
  React.useEffect(() => {
    if (redirectLink && redirectLink.trim() !== "") {
      // Validate URL format
      try {
        new URL(redirectLink);
        onUrlChanged?.(redirectLink);
      } catch {
        onUrlChanged?.(null);
      }
    } else {
      onUrlChanged?.(null);
    }
  }, [redirectLink, onUrlChanged]);

  const submit = async ({ fileUrl }: FormValues) => {
    const isInstagramUrl = isInstagram(fileUrl);
    const isFacebookReelUrl = isFacebookReel(fileUrl);
    const isFacebookVideoUrl = isFacebookVideo(fileUrl);
    const isFacebookShareUrlDetected = isFacebookShareUrl(fileUrl);
    const isFacebookUrl = isFacebookReelUrl || isFacebookVideoUrl || isFacebookShareUrlDetected;
    const isTikTokUrl = isTikTok(fileUrl);
    const isTwitterUrl = isTwitter(fileUrl);
    const isLinkedInUrl = isLinkedIn(fileUrl);
    const isPinterestUrl = isPinterest(fileUrl);
    const isSocialEmbedUrl =
      isInstagramUrl ||
      isTikTokUrl ||
      isTwitterUrl ||
      isLinkedInUrl ||
      isPinterestUrl ||
      isFacebookUrl;
    const canPlayUrl = ReactPlayer.canPlay(fileUrl);

    let file: UploadType | null = null;

    try {
      const response = await getEmbededDetails(fileUrl);
      file = response.data.data;
    } catch {
      file = null;
    }

    const isValidBackendFile =
      file &&
      (file.mimeType.includes("video") ||
        (!file.mimeType.includes("image") &&
          ![
            "png",
            "jpg",
            "jpeg",
            "pdf",
            "svg",
            "gif",
            "ai",
            "eps",
            "raw",
            "indd",
            "tiff",
          ].includes(
            file?.fileUrl
              ?.slice(
                file?.fileUrl?.lastIndexOf(".") + 1,
                file?.fileUrl?.length
              ) || ""
          )));

    if (isValidBackendFile && file) {
      onFileChanged(file);
      setSelectedFile(file);
      return;
    }

    if (isSocialEmbedUrl || canPlayUrl) {
      const embedFile: UploadType = {
        _id: "embeded-url",
        fileName: fileUrl,
        filePath: "",
        mimeType: "video/embed",
        updatedAt: new Date().toISOString(),
        actionType: ResourceType.Embeded,
        fileUrl,
      };

      onFileChanged(embedFile);
      setSelectedFile(embedFile);
      return;
    }

    toast.error("Please enter a valid video url");
  };

  return (
    <>
      <Controller<FormValues>
        control={control}
        name="fileUrl"
        render={({ field: { onChange, value } }) => (
          <Fragment>
            <Box
              sx={{ paddingBottom: 10, fontStyle: "italic" }}
              style={{ opacity: 0.7 }}
            >
              Please provide a video URL, the thumbnail image will be populated
              automatically.
            </Box>
            <Input
              className="mb-3"
              label="Please enter url path"
              onChange={(e: any) => {
                if (e.currentTarget.value.length === 0) setSelectedFile(null);
                onChange(e);
                handleSubmit(submit)();
              }}
              value={value}
              error={!!errors.fileUrl}
              helperText={errors.fileUrl?.message}
              fullWidth
            />
          </Fragment>
        )}
      />
      <Controller<FormValues>
        control={control}
        name="redirectLink"
        render={({ field: { onChange, value } }) => (
          <Input
            className="mb-3"
            label="Optional: Redirect URL (opens on click)"
            onChange={(e: any) => {
              onChange(e);
            }}
            value={value || ""}
            error={!!errors.redirectLink}
            helperText={errors.redirectLink?.message}
            fullWidth
            placeholder="https://example.com"
          />
        )}
      />
      <Box height="auto" minHeight="20em" display="grid">
        {selectedFile && previewUrl && isInstagram(previewUrl) && (
          <div className="social-embed-preview">
            <InstagramEmbed url={previewUrl} width={328} />
          </div>
        )}
        {selectedFile && previewUrl && isTikTok(previewUrl) && (
          <div className="social-embed-preview">
            <TikTokEmbed url={previewUrl} width={325} />
          </div>
        )}
        {selectedFile && previewUrl && isTwitter(previewUrl) && (
          <div className="social-embed-preview">
            <TwitterEmbed url={previewUrl} width={325} />
          </div>
        )}
        {selectedFile && previewUrl && isLinkedIn(previewUrl) && (
          <div className="social-embed-preview social-embed-preview--linkedin">
            <iframe
              title="LinkedIn embed"
              src={getLinkedInEmbedUrl(previewUrl)}
              width="100%"
              height="700"
              frameBorder="0"
              allowFullScreen
            />
          </div>
        )}
        {selectedFile && previewUrl && isPinterest(previewUrl) && (
          <div className="social-embed-preview">
            <PinterestEmbed url={previewUrl} width={325} />
          </div>
        )}
        {selectedFile && previewUrl && isFacebookShareUrl(previewUrl) && (
          <div className="social-embed-preview">
            <iframe
              title="Facebook share video"
              src={getFacebookVideoEmbedUrl(previewUrl)}
              width="100%"
              height="500"
              style={{ border: "none", overflow: "hidden" }}
              scrolling="no"
              frameBorder="0"
              allowFullScreen
              allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
            />
          </div>
        )}
        {selectedFile && previewUrl && (isFacebookReel(previewUrl) || isFacebookVideo(previewUrl)) && !isFacebookShareUrl(previewUrl) && (
          <div className="social-embed-preview">
            <FacebookEmbed url={previewUrl} width={280} />
          </div>
        )}
        {selectedFile &&
          (!previewUrl ||
            (!isInstagram(previewUrl) &&
              !isTikTok(previewUrl) &&
              !isTwitter(previewUrl) &&
              !isLinkedIn(previewUrl) &&
              !isPinterest(previewUrl) &&
              !isFacebookReel(previewUrl) &&
              !isFacebookVideo(previewUrl) &&
              !isFacebookShareUrl(previewUrl))) && (
            <FileReader file={selectedFile} videoConfig={{ controls: true }} />
          )}
        {!selectedFile && (
          <Box
            sx={{
              height: "100%",
              minHeight: "20em",
              border: "2px dashed #C4C4C4",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <MovieIcon fontSize="large" opacity={0.6} />
          </Box>
        )}
      </Box>
    </>
  );
};
